import {
  BitBucketIntegrationInfo,
  BitBucketWorkspace,
  GitProvider,
  GitRepository,
  IntegrationInfo
} from "@/pb/api/devplan/types/integrations";
import {ProviderType} from "@/app/api/integrations/connect/types";

export function getUnparsedIntegrationGitRepos(info: string | undefined | null): GitRepository[] {
  return getIntegrationGitRepos(info ? IntegrationInfo.fromJSON(JSON.parse(info)) : undefined)
}

export function getIntegrationGitRepos(info: IntegrationInfo | undefined | null): GitRepository[] {
  switch (info?.info?.$case) {
    case "github":
      return info.info.value.repositories
    case "bitBucket":
      return getAllBitBucketRepositories(info.info.value)
    case "gitlab":
      return info.info.value.repositories
  }
  return []
}

export function getGitIntegrationProvider(gitProvider: GitProvider): ProviderType {
  switch (gitProvider) {
    case GitProvider.GITHUB: return "github"
    case GitProvider.BIT_BUCKET: return "bitbucket"
    case GitProvider.GITLAB: return "gitlab"
    default: throw new Error(`Unexpected git provider: ${gitProvider}`)
  }
}

export function getAllBitBucketWorkspaces(bbIntegration: BitBucketIntegrationInfo): BitBucketWorkspace[] {
  const all = [...bbIntegration.availableWorkspaces, ...bbIntegration.integrations.map(i => i.workspace)]
  const unique: BitBucketWorkspace[] = []
  const seen = new Set<string>()
  all.forEach(w => {
    if (w && !seen.has(w.slug)) {
      seen.add(w.slug)
      unique.push(w)
    }
  })
  return unique
}

export function getAllBitBucketRepositories(bbIntegration: BitBucketIntegrationInfo): GitRepository[] {
  return bbIntegration.integrations.flatMap(i => i.repositories)
}

export function getProviderName(p : GitProvider): string {
  switch (p) {
    case GitProvider.BIT_BUCKET: return "bitbucket"
    case GitProvider.GITHUB: return "github"
    case GitProvider.GITLAB: return "gitlab"
    default: return "github"
  }
}

export function repoProvider(repo: string, providerMap: Record<string, GitProvider> | undefined): GitProvider {
  if (!providerMap) return GitProvider.GITHUB
  if (!repo.includes("/")) return GitProvider.GITHUB
  return providerMap[repo] ?? GitProvider.GITHUB
}

export function prUrl(repo: string, number: number, provider: GitProvider): string {
  switch (provider) {
    case GitProvider.BIT_BUCKET:
      return `https://bitbucket.org/${repo}/pull-requests/${number}`
    case GitProvider.GITLAB:
      return `https://gitlab.com/${repo}/-/merge_requests/${number}`
    case GitProvider.GITHUB:
    default:
      return `https://github.com/${repo}/pull/${number}`
  }
}

export function commitUrl(repo: string, sha: string, provider: GitProvider): string {
  switch (provider) {
    case GitProvider.BIT_BUCKET:
      return `https://bitbucket.org/${repo}/commits/${sha}`
    case GitProvider.GITLAB:
      return `https://gitlab.com/${repo}/-/commit/${sha}`
    case GitProvider.GITHUB:
    default:
      return `https://github.com/${repo}/commit/${sha}`
  }
}

export function buildRepoProviderMap(
  githubInfo: IntegrationInfo | undefined | null,
  bitbucketInfo: IntegrationInfo | undefined | null,
  gitlabInfo?: IntegrationInfo | undefined | null,
): Record<string, GitProvider> {
  const map: Record<string, GitProvider> = {}
  for (const repo of getIntegrationGitRepos(gitlabInfo)) {
    if (repo.fullName) map[repo.fullName] = GitProvider.GITLAB
  }
  for (const repo of getIntegrationGitRepos(bitbucketInfo)) {
    if (repo.fullName) map[repo.fullName] = GitProvider.BIT_BUCKET
  }
  for (const repo of getIntegrationGitRepos(githubInfo)) {
    if (repo.fullName) map[repo.fullName] = GitProvider.GITHUB
  }
  return map
}
